import { type ReactNode } from 'react';
import {
  Button,
  RequiredVideo,
  RequiredVideoPlayer,
  useRequiredVideoStatus,
} from '@/components/ui';

type Props = {
  id: string;
  videoKey: string;
  watched: boolean;
  onVideoCompleted: () => void;
  onClick: () => void;
  children: ReactNode;
};

const READY_LABEL = 'Todo claro... A JUGARLA';
const WAIT_LABEL = 'Para continuar tienes que ver el vídeo';
const CAPTION_TXT = 'text-center text-xs text-text-secondary';

function formatUnlock(secs: number): string {
  const total = Math.max(0, Math.ceil(secs));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

function UnlockCaption(): ReactNode {
  const { completed, remainingSecs, loading, available } =
    useRequiredVideoStatus();
  if (completed) return null;
  if (loading)
    return <p className={CAPTION_TXT}>Cargando v&iacute;deo&hellip;</p>;
  if (!available) return null;
  if (remainingSecs <= 0) {
    return <p className={CAPTION_TXT}>Pulsa Reproducir para empezar</p>;
  }
  return (
    <p className={CAPTION_TXT}>
      Disponible en{' '}
      <span className="font-mono tabular-nums">
        {formatUnlock(remainingSecs)}
      </span>
    </p>
  );
}

function GateButton(p: { watched: boolean; onClick: () => void }): ReactNode {
  return (
    <div className="space-y-xs">
      <Button
        size="lg"
        className="w-full"
        disabled={!p.watched}
        onClick={p.onClick}
      >
        {p.watched ? READY_LABEL : WAIT_LABEL}
      </Button>
      <UnlockCaption />
    </div>
  );
}

export function GameRulesSection(props: Props): ReactNode {
  return (
    <section
      id={props.id}
      className="flex min-h-dvh flex-col items-center justify-center px-base py-2xl"
    >
      <div className="w-full max-w-[640px] space-y-xl sm:max-w-[720px] lg:max-w-[820px]">
        <RequiredVideo
          videoKey={props.videoKey}
          onWatched={props.onVideoCompleted}
        >
          <RequiredVideoPlayer />
          <GateButton watched={props.watched} onClick={props.onClick} />
          <div className="space-y-lg">{props.children}</div>
        </RequiredVideo>
      </div>
    </section>
  );
}
